import type { ColDef } from 'ag-grid-community'
import { SupplierNameClickableCellRenderer } from './renderers/SupplierNameClickableCellRenderer'
import { InvoiceNumberClickableCellRenderer } from './renderers/InvoiceNumberClickableCellRenderer'

export function getPastDueColumnDefs(groupByDate: boolean): ColDef[] {
  const columns: ColDef[] = []

  if (groupByDate) {
    // For row grouping, add aging bucket field for grouping
    columns.push({
      headerName: 'AGING BUCKET',
      field: 'aging_bucket',
      rowGroup: true,
      hide: true, // Hide the column but keep it for grouping
      enableRowGroup: true,
      suppressMovable: true, // Prevent dragging
      suppressMenu: true, // Remove column menu
      lockPosition: true, // Lock column position
    })

    // Supplier column with proper configuration
    columns.push({
      headerName: 'SUPPLIER',
      field: 'supplier_name',
      flex: 2,
      minWidth: 180,
      headerClass: 'ag-header-cell-white-no-border',
      cellRenderer: SupplierNameClickableCellRenderer,
      cellStyle: { fontSize: '12px', color: '#212529', fontWeight: 'bold' },
    })
  } else {
    // For non-grouped mode, show supplier column
    columns.push({
      headerName: 'SUPPLIER',
      field: 'supplier_name',
      flex: 1.5,
      minWidth: 130,
      headerClass: 'ag-header-cell-white-no-border',
      cellRenderer: SupplierNameClickableCellRenderer,
      cellStyle: { fontSize: '12px', color: '#212529', fontWeight: 'bold' },
    })
  }

  columns.push(
    {
      headerName: 'BOLS',
      field: 'past_due_bol_count',
      flex: 0,
      width: 70,
      headerClass: 'ag-header-cell-white-no-border',
      cellStyle: { textAlign: 'center', fontSize: '12px', color: '#212529' },
      aggFunc: 'sum',
    },
    {
      headerName: 'INVOICES',
      field: 'past_due_invoice_count',
      flex: 0,
      width: 80,
      headerClass: 'ag-header-cell-white-no-border',
      cellStyle: { textAlign: 'center', fontSize: '12px', color: '#212529' },
      aggFunc: 'sum',
    },
    {
      headerName: 'MATCHED',
      field: 'matched_count',
      flex: 0,
      width: 80,
      headerClass: 'ag-header-cell-white-no-border',
      cellStyle: { textAlign: 'center', fontSize: '12px', color: '#212529' },
      aggFunc: 'sum',
    },
    {
      headerName: '$OUTSTANDING',
      field: 'past_due_amount',
      flex: 1,
      minWidth: 120,
      headerClass: 'ag-header-cell-white-no-border',
      cellStyle: { textAlign: 'right', fontSize: '12px', color: '#212529', fontWeight: 'bold' },
      pinned: 'right',
      aggFunc: 'sum',
      valueFormatter: (params) => {
        const amount = params.value || 0
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      },
    }
  )

  return columns
}

export const top10ColumnDefs: ColDef[] = [
  {
    headerName: 'SUPPLIER',
    field: 'supplier_name',
    flex: 1.5,
    minWidth: 120,
    headerClass: 'ag-header-cell-text',
    cellRenderer: SupplierNameClickableCellRenderer,
  },
  {
    headerName: 'BOLS',
    field: 'bol_count',
    flex: 1,
    minWidth: 60,
    maxWidth: 80,
    headerClass: 'ag-header-cell-text',
    cellStyle: { textAlign: 'center', fontSize: '12px', color: '#212529' },
  },
  {
    headerName: 'INVOICES',
    field: 'invoice_count',
    flex: 1,
    minWidth: 70,
    maxWidth: 100,
    headerClass: 'ag-header-cell-text',
    cellStyle: { textAlign: 'center', fontSize: '12px', color: '#212529' },
  },
  {
    headerName: 'AMOUNT DUE',
    field: 'amount_due',
    flex: 1,
    minWidth: 100,
    headerClass: 'ag-header-cell-text',
    cellStyle: { textAlign: 'right', fontSize: '12px', color: '#212529', fontWeight: 'bold' },
    pinned: 'right',
    valueFormatter: (params) => {
      const amount = params.value || 0
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
  },
]

export const latePaymentColumnDefs: ColDef[] = [
  {
    headerName: 'INVOICE',
    field: 'invoice_number',
    flex: 1.2,
    minWidth: 100,
    headerClass: 'ag-header-cell-text',
    cellRenderer: InvoiceNumberClickableCellRenderer,
  },
  {
    headerName: 'SUPPLIER',
    field: 'supplier_name',
    flex: 1,
    minWidth: 120,
    headerClass: 'ag-header-cell-text',
    cellStyle: { fontSize: '12px', color: '#212529' },
  },
  {
    headerName: 'PAYMENT DATE',
    field: 'payment_date',
    flex: 1,
    minWidth: 120,
    headerClass: 'ag-header-cell-text',
    cellStyle: { textAlign: 'center', fontSize: '12px', color: '#212529' },
  },
  {
    headerName: 'AMOUNT DUE',
    field: 'amount_due',
    flex: 1,
    minWidth: 100,
    headerClass: 'ag-header-cell-text',
    cellStyle: { textAlign: 'right', fontSize: '12px', color: '#212529', fontWeight: 'bold' },
    pinned: 'right',
    valueFormatter: (params) => {
      const amount = params.value || 0
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
  },
]
